<template>
  <div class="imports-history-page">
    <PageHeader
      :title="text.imports.history.page.title"
      :subtitle="text.imports.history.page.subtitle"
      :actions="[]"
    />

    <section class="app-filter-toolbar imports-history-filter-toolbar">
      <div class="app-filter-toolbar-row">
        <div class="app-filter-field imports-history-filter-toolbar__field">
          <span>{{ text.imports.history.filters.statusLabel }}</span>
          <multiselect
            v-model="statusFilterOption"
            :options="statusOptions"
            track-by="value"
            label="label"
            :multiple="false"
            :close-on-select="true"
            :clear-on-select="false"
            :preserve-search="true"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-filter-select app-select app-select--filters"
            :placeholder="text.imports.history.filters.statusPlaceholder"
            @input="fetchHistory"
          />
        </div>
      </div>
    </section>

    <section class="app-table-wrap imports-history-table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table">
          <thead>
            <tr>
              <th>{{ text.imports.history.table.headers.id }}</th>
              <th>{{ text.imports.history.table.headers.folio }}</th>
              <th>{{ text.imports.history.table.headers.module }}</th>
              <th>{{ text.imports.history.table.headers.status }}</th>
              <th>{{ text.imports.history.table.headers.user }}</th>
              <th>{{ text.imports.history.table.headers.executedAt }}</th>
              <th>{{ text.imports.history.table.headers.document }}</th>
              <th>{{ text.imports.history.table.headers.actions }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>{{ row.id }}</td>
              <td>{{ resolveFolio(row) }}</td>
              <td>{{ resolveModuleLabel(row.module) }}</td>
              <td>{{ resolveStatusLabel(row.status) }}</td>
              <td>{{ resolveUserLabel(row) }}</td>
              <td>{{ formatDateHuman(row.created_at) }}</td>
              <td class="app-table-actions-cell">
                <div class="app-table-actions-inline">
                  <button
                    v-if="resolvePrimaryFile(row)"
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--view"
                    :title="text.imports.history.table.actions.downloadDocument"
                    @click="downloadDocument(row)"
                  >
                    <i class="fa fa-download" aria-hidden="true"></i>
                  </button>
                  <span v-else class="app-muted">-</span>
                </div>
              </td>
              <td class="app-table-actions-cell">
                <div class="app-table-actions-inline">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--view"
                    :title="text.imports.history.table.actions.detail"
                    @click="openDetail(row.id)"
                  >
                    <i class="fa fa-eye" aria-hidden="true"></i>
                  </button>
                  <button
                    v-if="canShowRollbackAction(row)"
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--edit"
                    :title="loadingRollbackId === row.id ? text.imports.history.table.actions.rollingBack : text.imports.history.table.actions.rollback"
                    :disabled="loadingRollbackId === row.id"
                    @click="openRollbackModal(row)"
                  >
                    <i class="fa fa-undo" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="rows.length <= 0">
              <td colspan="8" class="app-table-empty">{{ text.imports.history.table.empty }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="detail" class="app-table-wrap imports-history-detail-wrap">
      <div class="imports-history-detail-wrap__content">
        <h4>{{ text.imports.history.detail.titlePrefix }}{{ resolveFolio(detail) }}</h4>
        <div v-for="file in detail.files || []" :key="file.id" class="imports-history-page__detail-file">
          <h5>{{ resolveSectionLabel(file.section_key) }} - {{ resolveStatusLabel(file.status) }}</h5>
          <p>{{ text.imports.history.detail.fileSummary(file.rows_total, file.rows_created, file.rows_updated, file.rows_errors) }}</p>
          <ul v-if="Array.isArray(file.changes) && file.changes.length > 0" class="imports-history-page__detail-changes">
            <li v-for="change in file.changes" :key="`change-${change.id}`">
              {{ text.imports.history.detail.rowChangeLine(
                change.row_number || 0,
                change.action || '',
                change.entity_label || '',
                Number(change.entity_id || 0),
                String(change.entity_summary || '')
              ) }}
            </li>
          </ul>
          <ul>
            <li v-for="message in file.messages || []" :key="message.id">
              [{{ message.type }}] Fila {{ message.row_number }} {{ message.column_name }} - {{ message.message }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <ConfirmActionModal
      :show="isRollbackConfirmModalOpen"
      :title="text.imports.history.rollbackModal.title"
      :subtitle="text.imports.history.rollbackModal.subtitle"
      :message="rollbackModalMessage"
      :confirm-text="rollbackModalConfirmText"
      :cancel-text="text.actions.common.cancel"
      @confirm="handleRollbackModalConfirm"
      @cancel="closeRollbackModal"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import importsService from '@/services/imports';
import { extractPayload } from '@/utils/dataNormalization';
import { formatDateTime } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'ImportsHistoryPage',
  components: {
    PageHeader,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      rows: [],
      detail: null,
      statusFilterOption: { value: '', label: text.imports.history.filters.options.all },
      loadingRollbackId: 0,
      isRollbackConfirmModalOpen: false,
      rollbackModalMode: 'confirm',
      pendingRollbackRow: null,
    };
  },
  computed: {
    statusOptions() {
      return [
        { value: '', label: this.text.imports.history.filters.options.all },
        { value: 'validated', label: this.text.imports.history.filters.options.validated },
        { value: 'validation_errors', label: this.text.imports.history.filters.options.validationErrors },
        { value: 'imported', label: this.text.imports.history.filters.options.imported },
        { value: 'imported_with_errors', label: this.text.imports.history.filters.options.importedWithErrors },
        { value: 'reverted', label: this.text.imports.history.filters.options.reverted },
      ];
    },
    rollbackModalMessage() {
      if (this.rollbackModalMode === 'expired') {
        return this.text.imports.history.rollbackModal.expiredMessage;
      }

      return this.text.imports.history.rollbackModal.confirmMessage;
    },
    rollbackModalConfirmText() {
      if (this.rollbackModalMode === 'expired') {
        return this.text.imports.history.rollbackModal.acknowledgeButton;
      }

      return this.text.imports.history.rollbackModal.confirmButton;
    },
  },
  mounted() {
    this.fetchHistory();
  },
  methods: {
    async fetchHistory() {
      try {
        const response = await importsService.history({
          status: this.statusFilterOption?.value || undefined,
        });

        const payload = extractPayload(response) || {};
        this.rows = Array.isArray(payload?.data) ? payload.data : [];
      } catch (error) {
        this.rows = [];
        this.$toast?.error(this.text.imports.history.toast.historyError);
      }
    },
    async openDetail(importId) {
      try {
        const response = await importsService.detail(importId);
        this.detail = extractPayload(response) || null;
      } catch (error) {
        this.$toast?.error(this.text.imports.history.toast.detailError);
      }
    },
    resolvePrimaryFile(row) {
      const files = Array.isArray(row?.files) ? row.files : [];
      return files[0] || null;
    },
    resolveFolio(row) {
      const folio = String(row?.folio || row?.token || '').trim();
      return folio !== '' ? folio : '-';
    },
    resolveModuleLabel(moduleKey) {
      const key = String(moduleKey || '').trim();
      const map = this.text.imports.history.table.moduleLabelByKey || {};

      return map[key] || key || '-';
    },
    resolveSectionLabel(sectionKey) {
      const key = String(sectionKey || '').trim();
      const sections = this.text?.imports?.modal?.sections || {};
      const label = String(sections?.[key]?.title || '').trim();
      return label !== '' ? label : key || '-';
    },
    resolveUserLabel(row) {
      const name = String(row?.created_by_name || '').trim();
      return name !== '' ? name : '-';
    },
    formatDateHuman(value) {
      return formatDateTime(value, { fallback: '-' });
    },
    parseApiDateTime(value) {
      const raw = String(value || '').trim();
      if (raw === '') {
        return NaN;
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const parsed = Date.parse(normalized);
      return Number.isNaN(parsed) ? NaN : parsed;
    },
    resolveRollbackLimitTimestamp(row) {
      const limitRaw = String(row?.rollback_available_until || '').trim();
      if (limitRaw !== '') {
        const parsedLimit = this.parseApiDateTime(limitRaw);
        if (!Number.isNaN(parsedLimit)) {
          return parsedLimit;
        }
      }

      const executedRaw = String(row?.executed_at || '').trim();
      const createdRaw = String(row?.created_at || '').trim();
      const fallbackBase = executedRaw !== '' ? executedRaw : createdRaw;
      const baseTime = this.parseApiDateTime(fallbackBase);
      if (Number.isNaN(baseTime)) {
        return 0;
      }

      return baseTime + (24 * 60 * 60 * 1000);
    },
    canShowRollbackAction(row) {
      return ['imported', 'imported_with_errors'].includes(String(row?.status || ''));
    },
    canRollback(row) {
      if (!row) {
        return false;
      }

      if (!['imported', 'imported_with_errors'].includes(String(row.status || ''))) {
        return false;
      }

      const limit = this.resolveRollbackLimitTimestamp(row);
      return limit > Date.now();
    },
    resolveStatusLabel(status) {
      const key = String(status || '').trim();
      const map = this.text.imports.history.table.statusLabelByKey || {};

      return map[key] || key || '-';
    },
    openRollbackModal(row) {
      if (!this.canShowRollbackAction(row)) {
        return;
      }

      this.pendingRollbackRow = row;
      this.rollbackModalMode = this.canRollback(row) ? 'confirm' : 'expired';
      this.isRollbackConfirmModalOpen = true;
    },
    closeRollbackModal() {
      this.isRollbackConfirmModalOpen = false;
      this.pendingRollbackRow = null;
      this.rollbackModalMode = 'confirm';
    },
    async handleRollbackModalConfirm() {
      if (this.rollbackModalMode === 'expired') {
        this.closeRollbackModal();
        return;
      }

      const importId = Number(this.pendingRollbackRow?.id || 0);
      if (importId <= 0) {
        this.closeRollbackModal();
        return;
      }

      this.closeRollbackModal();
      await this.rollback(importId);
    },
    async rollback(importId) {
      this.loadingRollbackId = importId;
      try {
        await importsService.rollback(importId);
        this.$toast?.success(this.text.imports.history.toast.rollbackSuccess);
        if (this.detail && Number(this.detail.id) === Number(importId)) {
          await this.openDetail(importId);
        }
        await this.fetchHistory();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || this.text.imports.history.toast.rollbackError);
      } finally {
        this.loadingRollbackId = 0;
      }
    },
    resolveFilenameFromHeaders(headers) {
      const contentDisposition = String(headers?.['content-disposition'] || '');
      const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
      if (!match) {
        return '';
      }

      const encoded = String(match[1] || match[2] || '').replace(/"/g, '').trim();
      try {
        return decodeURIComponent(encoded);
      } catch (error) {
        return encoded;
      }
    },
    async downloadDocument(row) {
      const file = this.resolvePrimaryFile(row);
      const importId = Number(row?.id || 0);
      const fileId = Number(file?.id || 0);
      if (importId <= 0 || fileId <= 0) {
        return;
      }

      try {
        const response = await importsService.downloadDocument(importId, fileId);
        const responseData = response?.data;
        const blob = responseData instanceof Blob
          ? responseData
          : (response instanceof Blob ? response : new Blob([responseData ?? '']));

        if (!(blob instanceof Blob) || blob.size <= 0) {
          throw new Error('Empty import document response');
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const fallbackName = String(file?.original_name || '').trim() || `importacion_${importId}_${fileId}.xlsx`;
        const fileName = this.resolveFilenameFromHeaders(response?.headers) || fallbackName;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        this.$toast?.error(this.text.imports.history.toast.downloadError);
      }
    },
  },
};
</script>

<style scoped>
.imports-history-page {
  display: grid;
  gap: 12px;
}

.imports-history-filter-toolbar {
  margin-bottom: 0;
}

.imports-history-filter-toolbar__field {
  min-width: 260px;
  max-width: 320px;
}

.imports-history-table-wrap,
.imports-history-detail-wrap {
  overflow: hidden;
}

.imports-history-detail-wrap__content {
  padding: 14px;
}

.imports-history-detail-wrap__content h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #334155;
}

.imports-history-page__detail-file {
  border-top: 1px dashed #cbd5e1;
  padding-top: 10px;
  margin-top: 10px;
}

.imports-history-page__detail-file h5 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
}

.imports-history-page__detail-file p {
  margin: 0 0 6px;
  font-size: 12px;
  color: #64748b;
}

.imports-history-page__detail-file ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
  font-size: 12px;
}

.imports-history-page__detail-changes {
  margin: 6px 0;
}
</style>
